import { HelpCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TaskConfig } from '../types';

interface OutputTabProps {
    config: TaskConfig;
    onResultPlacementChange: (placement: TaskConfig['resultPlacement']) => void;
    onResultPathChange: (path: string) => void;
    onOutputPathChange: (path: string) => void;
    readOnly: boolean;
}

export function OutputTab({
    config,
    onResultPlacementChange,
    onResultPathChange,
    onOutputPathChange,
    readOnly,
}: OutputTabProps) {
    return (
        <div className="p-6 space-y-8">
            {/* Result Placement */}
            <section className="space-y-4">
                <div className="flex items-center gap-2">
                    <Label className="text-base font-semibold">Result Placement</Label>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger>
                                <HelpCircle size={14} className="text-slate-400" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                                <p>
                                    How the task's output is stored in the workflow state.
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>

                <RadioGroup
                    value={config.resultPlacement}
                    onValueChange={(v) => onResultPlacementChange(v as TaskConfig['resultPlacement'])}
                    disabled={readOnly}
                    className="space-y-3"
                >
                    <div className={cn(
                        "flex items-start space-x-3 rounded-lg border p-4 cursor-pointer",
                        config.resultPlacement === 'merge' && "border-blue-300 bg-blue-50/50"
                    )}>
                        <RadioGroupItem value="merge" id="merge" className="mt-0.5" />
                        <div className="flex-1">
                            <label htmlFor="merge" className="font-medium cursor-pointer">
                                Merge under key
                            </label>
                            <p className="text-sm text-slate-500">
                                Store result at a specific path, preserving other state data.
                            </p>
                            {config.resultPlacement === 'merge' && (
                                <Input
                                    type="text"
                                    value={config.resultPath}
                                    onChange={(e) => onResultPathChange(e.target.value)}
                                    placeholder="$.result"
                                    className="mt-2 font-mono"
                                    disabled={readOnly}
                                />
                            )}
                        </div>
                    </div>

                    <div className={cn(
                        "flex items-start space-x-3 rounded-lg border p-4 cursor-pointer",
                        config.resultPlacement === 'replace' && "border-blue-300 bg-blue-50/50"
                    )}>
                        <RadioGroupItem value="replace" id="replace" className="mt-0.5" />
                        <div>
                            <label htmlFor="replace" className="font-medium cursor-pointer">
                                Replace entire state
                            </label>
                            <p className="text-sm text-slate-500">
                                Task output becomes the new workflow state.
                            </p>
                        </div>
                    </div>

                    <div className={cn(
                        "flex items-start space-x-3 rounded-lg border p-4 cursor-pointer",
                        config.resultPlacement === 'discard' && "border-blue-300 bg-blue-50/50"
                    )}>
                        <RadioGroupItem value="discard" id="discard" className="mt-0.5" />
                        <div>
                            <label htmlFor="discard" className="font-medium cursor-pointer">
                                Discard output
                            </label>
                            <p className="text-sm text-slate-500">
                                Task runs but its output is not stored.
                            </p>
                        </div>
                    </div>
                </RadioGroup>
            </section>

            {/* Output Filter */}
            <section className="space-y-3">
                <div className="flex items-center gap-2">
                    <Label className="text-base font-semibold">Output Filter</Label>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger>
                                <HelpCircle size={14} className="text-slate-400" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                                <p>
                                    JSONPath to filter the output after result placement.
                                    Applied before passing data to the next state.
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <Input
                    type="text"
                    value={config.outputPath || ''}
                    onChange={(e) => onOutputPathChange(e.target.value)}
                    placeholder="$ (pass everything)"
                    className="font-mono"
                    disabled={readOnly}
                />
                <p className="text-xs text-slate-500">
                    Leave empty to pass entire state. Use JSONPath to select specific fields.
                </p>
            </section>

            {/* Shape Output (simplified for v1) */}
            <section className="space-y-3">
                <div className="flex items-center gap-2">
                    <Label className="text-base font-semibold text-slate-400">
                        Shape Output
                    </Label>
                    <Badge variant="outline" className="text-[10px]">
                        Coming Soon
                    </Badge>
                </div>
                <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-400">
                    Field selection and renaming will be available in a future update.
                    <br />
                    For complex transformations, use a dedicated Transform step.
                </div>
            </section>
        </div>
    );
}

